import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'operationalTimerDurationMinutesInput',
    'operationalTimerDurationSecondsInput',
    'virtualTimerDurationMinutesInput',
    'virtualTimerDurationSecondsInput',
    'timerOptions',
    'calendarOptions',
    'timerRadioButton',
    'calendarRangeRadioButton',
    'endDateInput',
    'startDateInput',
    'startDatePicker',
    'endDatePicker'
  ];

  declare operationalTimerDurationMinutesInputTarget: HTMLInputElement;
  declare operationalTimerDurationSecondsInputTarget: HTMLInputElement;
  declare virtualTimerDurationMinutesInputTarget: HTMLInputElement;
  declare virtualTimerDurationSecondsInputTarget: HTMLInputElement;
  declare timerOptionsTarget: HTMLInputElement;
  declare calendarOptionsTarget: HTMLInputElement;
  declare timerRadioButtonTarget: HTMLInputElement;
  declare calendarRangeRadioButtonTarget: HTMLInputElement;
  declare endDateInputTarget: HTMLInputElement;
  declare startDateInputTarget: HTMLInputElement;
  declare startDatePickerTarget: HTMLElement;
  declare endDatePickerTarget: HTMLElement;

  connect(): void {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const vote_type = urlParams.get('vote_type')

    if (document.querySelectorAll('.elections.new').length) {
      const frame = this.element.closest('turbo-frame') as HTMLElement;
      const isOrgRefresh = frame?.dataset.orgRefreshed === 'true';

      if (isOrgRefresh) {
        delete frame.dataset.orgRefreshed;
      } else if (vote_type === 'motion') {
        this.timerRadioButtonTarget.click();
      } else {
        this.calendarRangeRadioButtonTarget.click();
      }
    }

    // Set up validation for start and end date/time
    this.setupDateTimeValidation();
  }

  private setupDateTimeValidation(): void {
    const startInput = this.startDatePickerTarget.querySelector(':scope .toolkit-date-picker__input') as HTMLInputElement;
    const endInput = this.endDatePickerTarget.querySelector(':scope .toolkit-date-picker__input') as HTMLInputElement;

    if (startInput && endInput) {
      // Listen for changes on start date to update end time options AND minimum date
      startInput.addEventListener('change', () => {
        setTimeout(() => {
          this.updateEndDateMinimum(startInput, endInput);
          this.filterEndTimeOptions();
        }, 100);
      });

      endInput.addEventListener('change', () => {
        setTimeout(() => this.filterEndTimeOptions(), 100);
      });

      // Update minimum date when end picker is focused (but only if not actively typing)
      endInput.addEventListener('focus', () => {
        // Only update if the input is empty or has a complete formatted value
        if (!endInput.value || this.parseDateTimeString(endInput.value)) {
          setTimeout(() => {
            this.updateEndDateMinimum(startInput, endInput);
            this.filterEndTimeOptions();
          }, 50);
        }
      });

      // Set initial minimum date if start date already has a value
      if (startInput.value) {
        setTimeout(() => this.updateEndDateMinimum(startInput, endInput), 100);
      }

      // Observe date picker containers for time select appearance
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if time selects were added
            const addedTimeSelects = Array.from(mutation.addedNodes).some(node =>
              node instanceof HTMLElement &&
              (node.classList?.contains('toolkit-date-picker__time') ||
               node.classList?.contains('picker__box') ||
               node.querySelector?.('.toolkit-date-picker__time'))
            );
            if (addedTimeSelects) {
              setTimeout(() => {
                this.filterEndTimeOptions();
                this.attachTimeSelectListeners();
              }, 100);
            }
          }
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  private attachTimeSelectListeners(): void {
    const endTimeContainer = document.querySelector('.picker--opened .toolkit-date-picker__time') as HTMLElement;
    if (!endTimeContainer) return;

    const hourSelect = endTimeContainer.querySelector('select[aria-label="Hour"]') as HTMLSelectElement;
    const minuteSelect = endTimeContainer.querySelector('select[aria-label="Minutes"]') as HTMLSelectElement;
    const ampmSelect = endTimeContainer.querySelector('select[aria-label="AM or PM"]') as HTMLSelectElement;

    if (hourSelect && !hourSelect.dataset.listenerAttached) {
      hourSelect.addEventListener('change', () => {
        setTimeout(() => this.filterEndTimeOptions(), 50);
      });
      hourSelect.dataset.listenerAttached = 'true';
    }

    if (minuteSelect && !minuteSelect.dataset.listenerAttached) {
      minuteSelect.addEventListener('change', () => {
        setTimeout(() => this.filterEndTimeOptions(), 50);
      });
      minuteSelect.dataset.listenerAttached = 'true';
    }

    if (ampmSelect && !ampmSelect.dataset.listenerAttached) {
      ampmSelect.addEventListener('change', () => {
        setTimeout(() => this.filterEndTimeOptions(), 50);
      });
      ampmSelect.dataset.listenerAttached = 'true';
    }
  }

  private filterEndTimeOptions(): void {
    const startInput = this.startDatePickerTarget.querySelector(':scope .toolkit-date-picker__input') as HTMLInputElement;
    const endInput = this.endDatePickerTarget.querySelector(':scope .toolkit-date-picker__input') as HTMLInputElement;

    if (!startInput?.value || !endInput?.value) {
      return;
    }

    const startDate = this.parseDateTimeString(startInput.value);
    const endDate = this.parseDateTimeString(endInput.value);

    if (!startDate || !endDate) {
      return;
    }

    // Check if dates are on the same day
    const sameDay = startDate.getFullYear() === endDate.getFullYear() &&
                    startDate.getMonth() === endDate.getMonth() &&
                    startDate.getDate() === endDate.getDate();

    if (!sameDay) {
      // Different days - restore all options
      this.restoreAllTimeOptions();
      return;
    }

    // Same day - filter end time options to be after start time
    this.filterTimeSelects(startDate);
  }

  private filterTimeSelects(startDate: Date): void {
    // Find the end date picker specifically - it should be the one that's open
    // We need to make sure we're filtering the END picker, not the START picker
    let endTimeContainer: HTMLElement | null = null;

    // Strategy 1: Look for the picker that's associated with the end date input
    const endPickerWrapper = this.endDatePickerTarget.querySelector('.picker') as HTMLElement;
    if (endPickerWrapper) {
      endTimeContainer = endPickerWrapper.querySelector('.toolkit-date-picker__time') as HTMLElement;
    }

    // Strategy 2: If not found, look for .picker--opened
    if (!endTimeContainer) {
      const openedPicker = document.querySelector('.picker--opened') as HTMLElement;
      if (openedPicker) {
        endTimeContainer = openedPicker.querySelector('.toolkit-date-picker__time') as HTMLElement;
      }
    }

    // Strategy 3: Look for any visible picker's time container
    if (!endTimeContainer) {
      const allPickers = document.querySelectorAll('.picker');
      for (const picker of Array.from(allPickers)) {
        if ((picker as HTMLElement).offsetParent !== null) { // is visible
          const timeContainer = picker.querySelector('.toolkit-date-picker__time') as HTMLElement;
          if (timeContainer) {
            endTimeContainer = timeContainer;
            break;
          }
        }
      }
    }

    if (!endTimeContainer) {
      return;
    }

    const hourSelect = endTimeContainer.querySelector('select[aria-label="Hour"]') as HTMLSelectElement;
    const minuteSelect = endTimeContainer.querySelector('select[aria-label="Minutes"]') as HTMLSelectElement;
    const ampmSelect = endTimeContainer.querySelector('select[aria-label="AM or PM"]') as HTMLSelectElement;

    if (!hourSelect || !minuteSelect) {
      return;
    }

    const is24Hour = ampmSelect === null;
    const startHour = startDate.getHours();
    const startMinute = startDate.getMinutes();

    const currentHourValue = hourSelect.value;
    const currentHour = parseInt(currentHourValue) || 0;
    const currentAmpm = ampmSelect?.value;

    if (is24Hour) {
      // 24-hour format: disable all hours < start hour (not <=)
      Array.from(hourSelect.options).forEach(option => {
        const hourValue = parseInt(option.value);
        const shouldDisable = hourValue < startHour; // Only disable hours BEFORE start hour
        option.disabled = shouldDisable;
        option.style.display = shouldDisable ? 'none' : '';
      });

      // If current selected hour is disabled, select first available hour
      if (currentHour < startHour) {
        const firstAvailableHour = Array.from(hourSelect.options).find(opt =>
          !opt.disabled && parseInt(opt.value) >= startHour
        );
        if (firstAvailableHour) {
          hourSelect.value = firstAvailableHour.value;
        }
      }

      // Update current hour value after potential change
      const updatedCurrentHour = parseInt(hourSelect.value) || 0;

      // If current hour equals start hour, filter minutes to be > start minute
      if (updatedCurrentHour === startHour) {
        Array.from(minuteSelect.options).forEach(option => {
          const minuteValue = parseInt(option.value);
          const shouldDisable = minuteValue <= startMinute;
          option.disabled = shouldDisable;
          option.style.display = shouldDisable ? 'none' : '';
        });

        // If current selected minute is disabled, select first available
        const currentMinute = parseInt(minuteSelect.value) || 0;
        if (currentMinute <= startMinute) {
          const firstAvailableMinute = Array.from(minuteSelect.options).find(opt =>
            !opt.disabled && parseInt(opt.value) > startMinute
          );
          if (firstAvailableMinute) {
            minuteSelect.value = firstAvailableMinute.value;
          }
        }
      } else if (updatedCurrentHour > startHour) {
        // Hour is after start hour, all minutes are valid
        this.restoreOptions(minuteSelect);
      }
    } else {
      // 12-hour format with AM/PM
      const startHour12 = this.toHour12(startHour);
      const startAmpm = startHour >= 12 ? 'PM' : 'AM';

      // Step 1: Filter AM/PM select
      if (ampmSelect) {

        // If start is PM, end must be PM (same day)
        // If start is AM, end can be AM or PM
        Array.from(ampmSelect.options).forEach(option => {
          const shouldDisable = startAmpm === 'PM' && option.value === 'AM';
          option.disabled = shouldDisable;
          option.style.display = shouldDisable ? 'none' : '';
        });

        // If current selection is AM but start is PM, change to PM
        if (startAmpm === 'PM' && currentAmpm === 'AM') {
          ampmSelect.value = 'PM';
          ampmSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // Get updated AM/PM value after potential change
      const updatedCurrentAmpm = ampmSelect?.value || currentAmpm;

      // Step 2: Filter hours based on AM/PM period
      if (updatedCurrentAmpm === startAmpm) {
        // Same AM/PM period - need to filter hours
        Array.from(hourSelect.options).forEach(option => {
          const hour12 = parseInt(option.value);
          let shouldDisable = false;

          // In 12-hour format, the cycle is: 12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11
          // We need to compare positions in this cycle
          const startPosition = startHour12 === 12 ? 0 : startHour12;
          const currentPosition = hour12 === 12 ? 0 : hour12;

          // Disable if current hour comes before start hour in the cycle
          shouldDisable = currentPosition < startPosition;

          option.disabled = shouldDisable;
          option.style.display = shouldDisable ? 'none' : '';
        });

        // If current selected hour is disabled, select first available
        const currentHour12 = parseInt(hourSelect.value);
        const currentHourOption = Array.from(hourSelect.options).find(opt => parseInt(opt.value) === currentHour12);

        if (currentHourOption?.disabled) {
          const firstAvailableHour = Array.from(hourSelect.options).find(opt => !opt.disabled);
          if (firstAvailableHour) {
            hourSelect.value = firstAvailableHour.value;
          }
        }

        // Get updated hour after potential change
        const updatedCurrentHour12 = parseInt(hourSelect.value);

        // Step 3: Filter minutes if same hour
        if (updatedCurrentHour12 === startHour12) {
          Array.from(minuteSelect.options).forEach(option => {
            const minuteValue = parseInt(option.value);
            const shouldDisable = minuteValue <= startMinute;
            option.disabled = shouldDisable;
            option.style.display = shouldDisable ? 'none' : '';
          });

          // If current selected minute is disabled, select first available
          const currentMinute = parseInt(minuteSelect.value) || 0;
          if (currentMinute <= startMinute) {
            const firstAvailableMinute = Array.from(minuteSelect.options).find(opt =>
              !opt.disabled && parseInt(opt.value) > startMinute
            );
            if (firstAvailableMinute) {
              minuteSelect.value = firstAvailableMinute.value;
            }
          }
        } else {
          // Different hour in same period - all minutes are valid
          this.restoreOptions(minuteSelect);
        }
      } else {
        // Different AM/PM period - end is PM, start was AM
        // All hours and minutes are valid
        this.restoreOptions(hourSelect);
        this.restoreOptions(minuteSelect);
      }
    }
  }

  private filterOptions(select: HTMLSelectElement, predicate: (value: string) => boolean): void {
    const currentValue = select.value;
    Array.from(select.options).forEach(option => {
      const shouldShow = predicate(option.value);
      option.disabled = !shouldShow;
      option.style.display = shouldShow ? '' : 'none';
    });

    // If current value is now filtered out, select first available
    if (select.value && !predicate(select.value)) {
      const firstAvailable = Array.from(select.options).find(opt => !opt.disabled);
      if (firstAvailable) {
        select.value = firstAvailable.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  private restoreOptions(select: HTMLSelectElement): void {
    Array.from(select.options).forEach(option => {
      option.disabled = false;
      option.style.display = '';
    });
  }

  private restoreAllTimeOptions(): void {
    const endTimeContainer = document.querySelector('.picker--opened .toolkit-date-picker__time') as HTMLElement;
    if (!endTimeContainer) return;

    const selects = endTimeContainer.querySelectorAll('select');
    selects.forEach(select => this.restoreOptions(select as HTMLSelectElement));
  }

  private toHour12(h24: number): number {
    let h = h24 % 12;
    if (h === 0) h = 12;
    return h;
  }

  private updateEndDateMinimum(startInput: HTMLInputElement, endInput: HTMLInputElement): void {
    if (!startInput.value) return;

    const startDate = this.parseDateTimeString(startInput.value);
    if (!startDate) return;

    // Find the hidden calendar input (not the visible full input)
    // The date picker component has both a "full" input (visible) and a "calendar" input (hidden, used by pickadate)
    // The calendar input has class "datepicker_input" and data-target="calendar"
    const calendarInput = this.endDatePickerTarget.querySelector(':scope .datepicker_input') as HTMLInputElement;

    if (!calendarInput) {
      return; // Silently fail if calendar not found (might not be initialized yet)
    }

    // Access the picker from the calendar input using jQuery
    const $calendarInput = $(calendarInput);

    try {
      const picker = $calendarInput.pickadate('picker');

      if (picker && typeof picker.set === 'function') {
        // Save current end date value before setting minimum
        const currentEndValue = endInput.value;
        const currentEndDate = this.parseDateTimeString(currentEndValue);

        // Only set minimum if end date is valid and after start date
        // This prevents pickadate from auto-adjusting valid end dates
        if (currentEndDate && currentEndDate >= startDate) {
          // End date is valid and after start date, safe to set minimum
          picker.set('min', [startDate.getFullYear(), startDate.getMonth(), startDate.getDate()]);

          // Ensure the visible input value hasn't been changed by pickadate
          if (endInput.value !== currentEndValue) {
            endInput.value = currentEndValue;
          }
        } else if (!currentEndDate || currentEndDate < startDate) {
          // End date is invalid or before start date, set minimum without current selection
          picker.set('min', [startDate.getFullYear(), startDate.getMonth(), startDate.getDate()]);
        }
      }
    } catch (e) {
      // Silently fail - picker might not be initialized yet
    }
  }

  private parseDateTimeString(dateTimeStr: string): Date | null {
    // Get the date and time formats from the component
    const datePickerDiv = this.element.querySelector('[data-date-format]');
    const dateFormat = datePickerDiv?.getAttribute('data-date-format');
    const timeFormat = datePickerDiv?.getAttribute('data-time-format');

    if (!dateFormat || !timeFormat) {
      return null;
    }

    // Detect separator (could be -, /, space, comma, or .)
    const separatorMatch = dateFormat.match(/[-\/\s,. ]/);
    const separator = separatorMatch ? separatorMatch[0] : '-';

    // Split format by separators to get components (including comma for formats like "MMMM D, YYYY")
    const formatParts = dateFormat.split(/[-\/\s,.]+/).filter(part => part.length > 0);

    // Build regex pattern and track component positions
    let groupIndex = 1;
    const componentMap: { [key: number]: string } = {}; // group index -> component type

    const patternParts = formatParts.map(part => {
      if (part === 'DD' || part === 'D') {
        componentMap[groupIndex] = 'day';
        groupIndex++;
        return '(\\d{1,2})';
      } else if (part === 'MM' || part === 'M') {
        componentMap[groupIndex] = 'month_num';
        groupIndex++;
        return '(\\d{1,2})';
      } else if (part === 'MMMM') {
        componentMap[groupIndex] = 'month_name';
        groupIndex++;
        return '(\\w+)';
      } else if (part === 'MMM') {
        componentMap[groupIndex] = 'month_abbr';
        groupIndex++;
        return '(\\w+)';
      } else if (part === 'YYYY' || part === 'YY') {
        componentMap[groupIndex] = 'year';
        groupIndex++;
        return '(\\d{2,4})';
      }
      return part;
    });

    // Use flexible separator pattern that matches any combination of separators
    // This handles formats like "MMMM D, YYYY" where separators vary
    const flexibleSeparator = '[\\s,\\/.-]+';
    const datePattern = patternParts.join(flexibleSeparator);

    // Build time pattern
    let timePattern: string;
    const timeStartGroup = groupIndex; // Where time groups start

    if (timeFormat === 'HH:mm') {
      // 24-hour format
      timePattern = '(\\d{1,2}):(\\d{2})';
      componentMap[groupIndex] = 'hour';
      componentMap[groupIndex + 1] = 'minute';
      groupIndex += 2;
    } else {
      // 12-hour format with AM/PM
      timePattern = '(\\d{1,2}):(\\d{2})\\s+(AM|PM)';
      componentMap[groupIndex] = 'hour';
      componentMap[groupIndex + 1] = 'minute';
      componentMap[groupIndex + 2] = 'ampm';
      groupIndex += 3;
    }

    // Combine into full pattern
    const fullPattern = new RegExp(`^${datePattern}\\s+${timePattern}$`);

    const match = dateTimeStr.match(fullPattern);

    if (!match) {
      return null;
    }

    // Extract components using the map
    let day: number | undefined;
    let month: number | undefined;
    let year: number | undefined;
    let hour: number | undefined;
    let minute: number | undefined;
    let ampm: string | undefined;

    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthsAbbr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    for (let i = 1; i < match.length; i++) {
      const componentType = componentMap[i];
      const value = match[i];

      switch (componentType) {
        case 'day':
          day = parseInt(value, 10);
          break;
        case 'month_num':
          month = parseInt(value, 10) - 1; // Convert to 0-indexed
          break;
        case 'month_name':
          month = months.indexOf(value);
          if (month === -1) month = undefined;
          break;
        case 'month_abbr':
          month = monthsAbbr.indexOf(value);
          if (month === -1) month = undefined;
          break;
        case 'year':
          year = parseInt(value, 10);
          // Handle 2-digit years
          if (year < 100) {
            year += year < 50 ? 2000 : 1900;
          }
          break;
        case 'hour':
          hour = parseInt(value, 10);
          break;
        case 'minute':
          minute = parseInt(value, 10);
          break;
        case 'ampm':
          ampm = value;
          break;
      }
    }

    // Validate we have all required components
    if (day === undefined || month === undefined || year === undefined ||
        hour === undefined || minute === undefined) {
      return null;
    }

    // Handle AM/PM conversion
    if (ampm) {
      if (ampm === 'PM' && hour !== 12) hour += 12;
      if (ampm === 'AM' && hour === 12) hour = 0;
    }

    const parsed = new Date(year, month, day, hour, minute);

    return parsed;
  }



  public enableTimerOptions(): void {
    this.operationalTimerDurationMinutesInputTarget.disabled = false;
    this.operationalTimerDurationSecondsInputTarget.disabled = false;
    this.timerOptionsTarget.classList.remove('hide');
    this.calendarOptionsTarget.classList.add('hide');
  }

  public enableCalendarOptions(): void {
    this.calendarOptionsTarget.classList.remove('hide');
    this.timerOptionsTarget.classList.add('hide');
    this.operationalTimerDurationMinutesInputTarget.disabled = true;
    this.operationalTimerDurationMinutesInputTarget.value = '03';
    this.operationalTimerDurationSecondsInputTarget.disabled = true;
    this.operationalTimerDurationSecondsInputTarget.value = '00';
    this.virtualTimerDurationMinutesInputTarget.value = '03'
    this.virtualTimerDurationSecondsInputTarget.value = '00'
  }

  public textIntoStartDateField(event) {
    event.preventDefault();
    event.stopPropagation();
    const text = event.target.textContent.trim();
    const displayInput = this.startDatePickerTarget.querySelector(':scope .toolkit-date-picker__input') as HTMLInputElement;
    const hiddenInput = this.startDatePickerTarget.querySelector(':scope input[type="hidden"]') as HTMLInputElement;

    if (displayInput) {
      // Parse the natural language date
      const parsedDate = chrono.parseDate(text);
      if (parsedDate) {
        // Format the date to match the expected format for display
        const formattedValue = this.formatDateForInput(parsedDate);
        displayInput.value = formattedValue;

        // Update hidden input with Rails-parseable format
        if (hiddenInput) {
          hiddenInput.value = this.formatDateTimeForRails(parsedDate);
        }

        // Trigger change event so DatePicker controller can update
        displayInput.dispatchEvent(new Event('change', { bubbles: true }));

        // Manually update the minimum date for end picker
        const endDisplayInput = this.endDatePickerTarget.querySelector(':scope .toolkit-date-picker__input') as HTMLInputElement;
        if (endDisplayInput) {
          this.updateEndDateMinimum(displayInput, endDisplayInput);
        }
      } else {
        // If parsing fails, just set the text value
        displayInput.value = text;
      }
    }
  }

  public textIntoEndDateField(event) {
    event.preventDefault();
    event.stopPropagation();
    const text = event.target.textContent.trim();
    const displayInput = this.endDatePickerTarget.querySelector(':scope .toolkit-date-picker__input') as HTMLInputElement;
    const hiddenInput = this.endDatePickerTarget.querySelector(':scope input[type="hidden"]') as HTMLInputElement;

    if (displayInput) {
      // Parse the natural language date
      const parsedDate = chrono.parseDate(text);
      if (parsedDate) {
        // Format the date to match the expected format for display
        const formattedValue = this.formatDateForInput(parsedDate);
        displayInput.value = formattedValue;

        // Update hidden input with Rails-parseable format
        if (hiddenInput) {
          hiddenInput.value = this.formatDateTimeForRails(parsedDate);
        }

        // Trigger change event so DatePicker controller can update
        displayInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // If parsing fails, just set the text value
        displayInput.value = text;
      }
    }
  }

  public toggleVirtualTimerOptions(): void {
    this.virtualTimerDurationMinutesInputTarget.toggleAttribute('disabled');
    this.virtualTimerDurationSecondsInputTarget.toggleAttribute('disabled');
  }

  public mirrorOperationalTimerMinutesToVirtualTimerMinutes(): void {
    this.virtualTimerDurationMinutesInputTarget.value =
      this.operationalTimerDurationMinutesInputTarget.value;
  }

  public mirrorOperationalTimerSecondsToVirtualTimerSeconds(): void {
    this.virtualTimerDurationSecondsInputTarget.value =
      this.operationalTimerDurationSecondsInputTarget.value;
  }

  // Format date for Rails without timezone information (YYYY-MM-DD HH:mm:ss)
  private formatDateTimeForRails(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  private formatDateForInput(date: Date): string {
    // Get format attributes from the datepicker element
    const datePickerDiv = this.element.querySelector('[data-date-format]');
    const dateFormat = datePickerDiv?.getAttribute('data-date-format');
    const timeFormat = datePickerDiv?.getAttribute('data-time-format');

    // Format the date part
    let datePart: string;
    if (!dateFormat) {
      // Fallback to default format
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      datePart = `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    } else {
      // Format date according to organization settings
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const monthName = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][date.getMonth()];

      const dd = String(day).padStart(2, '0');
      const mm = String(month).padStart(2, '0');
      const yyyy = String(year);

      datePart = dateFormat
        .replace('MMMM', monthName)
        .replace('DD', dd)
        .replace('MM', mm)
        .replace('YYYY', yyyy)
        .replace('D', String(day));
    }

    // Format the time part
    if (timeFormat === 'HH:mm') {
      // 24-hour format
      const paddedHours = date.getHours().toString().padStart(2, '0');
      const paddedMinutes = date.getMinutes().toString().padStart(2, '0');
      return `${datePart} ${paddedHours}:${paddedMinutes}`;
    } else {
      // 12-hour format with AM/PM
      const hours = date.getHours();
      const displayHour = hours % 12 || 12;
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const paddedMinutes = date.getMinutes().toString().padStart(2, '0');
      return `${datePart} ${displayHour}:${paddedMinutes} ${ampm}`;
    }
  }
}
